import type { User } from '@/api/types/user';
import { UserAPIs } from '@/api/users';
import { XMarkIcon } from '@heroicons/react/24/outline';
import React, { useEffect, useState, useCallback, useRef, type FC } from 'react';
import type { ChangeEvent } from 'react';
import { Button, ButtonSize, ButtonType } from './button';
import { CustomToast } from './custom-toast';
import { Modal } from './modal';
import { TextField, TextFieldType } from './text-field';
import { UploadIcon } from './upload-icon';

interface AccountSettingsModalProps {
    onAccountUpdated?: () => void;
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
}

export const AccountSettingsModal: FC<AccountSettingsModalProps> = ({ onAccountUpdated, isOpen, setIsOpen }) => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [name, setName] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [picture, setPicture] = useState<string>('');
    const [pickedPicture, setPickedPicture] = useState<File | null>(null);
    const [user, setUser] = useState<User | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const fetchUser = useCallback(async () => {
        try {
            const user = await UserAPIs.getCurrentUser();
            setUser(user);
            setName(`${user.firstName} ${user.lastName}`);
            setEmail(user.email);
            setPicture(user.profilePicture);
        } catch (error) {
            CustomToast.error('Unable to fetch user data');
        }
    }, []);

    const updateUser = useCallback(async () => {
        setIsLoading(true);
        try {
            const splittedName = name.split(' ');
            const firstName = splittedName.slice(0, -1).join(' ').trim();
            const lastName = splittedName[splittedName.length - 1].trim();

            await UserAPIs.updateUser(firstName, lastName, email, pickedPicture, () => {
                onAccountUpdated?.();
                CustomToast.success('Account updated successfully');
            });
        } catch (error) {
            CustomToast.error('Unable to update account');
        } finally {
            setIsLoading(false);
        }
    }, [onAccountUpdated, name, email, pickedPicture]);

    const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
        const file = event?.target?.files?.[0];
        if (file) {
            const validTypes = ['image/jpeg', 'image/png'];
            if (validTypes.includes(file.type)) {
                setPickedPicture(file);
            } else {
                CustomToast.error('Please select a JPEG or PNG file');
            }
        }
    };

    const closeModal = () => {
        setIsOpen(false);
        setPickedPicture(null);
    };

    useEffect(() => {
        fetchUser();
    }, [fetchUser]);

    return (
        <Modal isOpen={isOpen}>
            <div className="flex flex-col absolute min-w-96 w-[40vw] bg-gray-150 shadow-xl rounded-xl transition-all duration-300 ease-in-out">
                <div className="flex flex-row p-3 justify-between items-center">
                    <p className="text-xl text-white">Account Settings</p>
                    <XMarkIcon
                        className="h-5 w-5 text-white hover:cursor-pointer hover:text-gray-30"
                        onClick={() => closeModal()}
                    />
                </div>
                <hr className="bg-gray-100 border-gray-100 w-full" />
                <div className="flex flex-row items-center">
                    <div className="flex flex-col px-4 w-full">
                        <p className="ml-1 mb-1 text-white text-sm">Name</p>
                        <TextField
                            id="name"
                            required={true}
                            type={TextFieldType.Text}
                            value={name}
                            placeholder="Name"
                            onChange={(value) => {
                                setName(value);
                            }}
                        />
                    </div>
                    <input
                        id="picture"
                        type="file"
                        accept="image/jpeg, image/png"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        multiple={false}
                        style={{ display: 'none' }}
                    />
                    <div className="flex flex-col px-4 py-2 gap-2 w-36">
                        <p className="text-xs text-gray-50">Profile Picture</p>
                        <div className="relative w-[72px] h-[72px] rounded-full overflow-hidden bg-gray-100 group">
                            {(picture || pickedPicture) && (
                                <img
                                    src={pickedPicture ? URL.createObjectURL(pickedPicture) : picture}
                                    alt="Profile"
                                    className="w-full h-full object-cover"
                                />
                            )}
                            <button
                                type="button"
                                className="absolute inset-0"
                                onClick={() => fileInputRef.current?.click()}
                            >
                                <div
                                    className={`absolute inset-0 ${picture || pickedPicture ? 'bg-black bg-opacity-80 opacity-0' : ''} flex items-center justify-center transition-opacity duration-300 group-hover:opacity-100`}
                                >
                                    <UploadIcon className="h-5 w-5 stroke-white" />
                                </div>
                            </button>
                        </div>
                    </div>
                </div>
                <div className="flex flex-col px-4 pb-4 w-full">
                    <p className="ml-1 mb-1 text-white text-sm">Email</p>
                    <TextField
                        id="email"
                        required={true}
                        type={TextFieldType.Email}
                        value={email}
                        placeholder="Email"
                        onChange={(value) => {
                            setEmail(value);
                        }}
                    />
                </div>
                <hr className="bg-gray-100 border-gray-100 w-full mt-2" />
                <div className="w-full flex flex-row justify-end gap-3 p-6">
                    <Button
                        title="Cancel"
                        className="hover:bg-gray-160"
                        buttonType={ButtonType.SECONDARY}
                        buttonSize={ButtonSize.LARGE}
                        disabled={isLoading}
                        onClick={() => closeModal()}
                    />
                    <Button
                        title="Done"
                        buttonType={ButtonType.PRIMARY}
                        buttonSize={ButtonSize.LARGE}
                        disabled={isLoading}
                        onClick={async () => {
                            await updateUser();
                            closeModal();
                        }}
                    />
                </div>
            </div>
        </Modal>
    );
};
